import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { 
  Plus, Edit2, Save, Info, AlignLeft, 
  Power, RefreshCw, DollarSign, MessageSquareText, Smartphone 
} from 'lucide-react';

const OriginMethods = () => {
  const [methods, setMethods] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // 🔥 Se añadió 'is_push' al estado inicial
  const initialFormState = { 
    country_code: '', 
    method: '', 
    price: '', 
    comment: '',
    data: '', 
    instructions: '',
    active: true,
    is_push: false
  };

  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => { fetchMethods(); }, []);

  const fetchMethods = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('payment_methods_origin')
      .select('*')
      .order('active', { ascending: false }) 
      .order('created_at', { ascending: false });
    setMethods(data || []);
    setLoading(false);
  };

  const handleSave = async () => {
    if (!formData.country_code || !formData.method) {
      alert("País y Método son obligatorios");
      return;
    }

    setLoading(true);
    try {
      let jsonPayload;
      try {
        jsonPayload = typeof formData.data === 'string' ? JSON.parse(formData.data) : formData.data;
      } catch (e) {
        alert("Error en DATA BANCARIA: Debe ser un JSON válido.");
        setLoading(false);
        return;
      }

      // 🔥 Se incluye is_push en el envío a la BD
      const payload = { 
        country_code: formData.country_code.toUpperCase(), 
        method: formData.method, 
        price: formData.price === '' ? 0 : parseFloat(formData.price),
        comment: formData.comment,
        data: jsonPayload, 
        instructions: formData.instructions,
        active: formData.active,
        is_push: formData.is_push
      };

      if (editingId) {
        await supabase.from('payment_methods_origin').update(payload).eq('id', editingId);
      } else {
        await supabase.from('payment_methods_origin').insert([payload]);
      }
      
      setEditingId(null);
      setFormData(initialFormState);
      fetchMethods();
    } catch (err) {
      console.error("Error saving:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    const { error } = await supabase
      .from('payment_methods_origin')
      .update({ active: !currentStatus })
      .eq('id', id);
    
    if (!error) fetchMethods();
    else alert("Error al cambiar estado: " + error.message);
  };

  const handleEdit = (method) => {
    setEditingId(method.id);
    setFormData({
      country_code: method.country_code,
      method: method.method,
      price: method.price !== null ? method.price : '', 
      comment: method.comment || '', 
      data: JSON.stringify(method.data, null, 2),
      instructions: method.instructions || '',
      active: method.active,
      is_push: method.is_push || false // 🔥 Carga el estado PUSH
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderDataSummary = (data) => {
    if (!data) return <span className="text-slate-300 italic text-xs">Sin datos</span>;
    return (
      <div className="text-[10px] leading-tight font-mono bg-white p-2 rounded-lg border border-slate-100 shadow-sm max-w-[180px]">
        {Object.entries(data).slice(0, 3).map(([key, value]) => (
          <div key={key} className="truncate">
            <span className="text-blue-500 font-bold uppercase">{key}:</span> {String(value)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-4 md:p-8 animate-in fade-in duration-500">
      
      {/* FORMULARIO */}
      <div className={`mb-10 p-6 md:p-8 rounded-[32px] border-2 transition-all duration-500 ${editingId ? 'bg-indigo-50 border-indigo-200 shadow-indigo-100' : 'bg-white border-slate-100'} shadow-xl`}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-black flex items-center gap-2 text-slate-800 tracking-tight">
            {editingId ? <Edit2 className="w-5 h-5 text-indigo-500" /> : <Plus className="w-5 h-5 text-blue-500" />} 
            {editingId ? 'MODIFICAR MÉTODO' : 'NUEVO PUNTO DE RECEPCIÓN'}
          </h3>
          <button onClick={fetchMethods} className="p-2.5 bg-slate-50 border border-slate-100 rounded-full text-slate-400 hover:text-blue-600 hover:bg-white transition-colors shadow-sm active:scale-95">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="space-y-1">
            <label className="text-[10px] font-black text-slate-400 ml-1 tracking-widest">PAÍS</label>
            <input placeholder="Ej: CO" className="w-full p-3.5 border border-slate-200 bg-slate-50 rounded-xl font-bold uppercase focus:bg-white focus:border-blue-400 outline-none transition-all shadow-sm" value={formData.country_code} onChange={e => setFormData({...formData, country_code: e.target.value})} />
          </div>
          
          <div className="space-y-1">
            <label className="text-[10px] font-black text-slate-400 ml-1 tracking-widest">PLATAFORMA</label>
            <input placeholder="Zelle / Banesco" className="w-full p-3.5 border border-slate-200 bg-slate-50 rounded-xl font-bold focus:bg-white focus:border-blue-400 outline-none transition-all shadow-sm" value={formData.method} onChange={e => setFormData({...formData, method: e.target.value})} />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-black text-slate-400 ml-1 tracking-widest flex items-center gap-1"><DollarSign className="w-3 h-3"/> PRECIO / COMISIÓN</label>
            <input type="number" placeholder="Ej: 5000 (Deja en 0 para Gratis)" className="w-full p-3.5 border border-slate-200 bg-slate-50 rounded-xl font-bold focus:bg-white focus:border-blue-400 outline-none transition-all shadow-sm text-green-600" value={formData.price} onChange={e => setFormData({...formData, price: e.target.value})} />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-black text-slate-400 ml-1 tracking-widest flex items-center gap-1"><MessageSquareText className="w-3 h-3"/> COMENTARIO (AZUL)</label>
            <input placeholder="Ej: Cae al instante..." className="w-full p-3.5 border border-slate-200 bg-slate-50 rounded-xl font-bold focus:bg-white focus:border-blue-400 outline-none transition-all shadow-sm text-blue-600" value={formData.comment} onChange={e => setFormData({...formData, comment: e.target.value})} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <div className="space-y-1">
            <label className="text-[10px] font-black text-slate-400 ml-1 tracking-widest flex items-center gap-1">CONFIGURACIÓN (JSON) <Info className="w-3 h-3"/></label>
            <textarea placeholder='{"banco": "Banesco", "cuenta": "0134..."}' className="w-full p-4 border border-slate-200 bg-slate-50 focus:bg-white rounded-2xl font-mono text-xs focus:border-blue-400 outline-none transition-all shadow-sm min-h-[100px]" value={formData.data} onChange={e => setFormData({...formData, data: e.target.value})} />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-black text-slate-400 ml-1 tracking-widest flex items-center gap-1"><AlignLeft className="w-3 h-3" /> INSTRUCCIONES VISIBLES AL CLIENTE</label>
            <textarea placeholder="Ej: Realizar el pago y enviar capture..." className="w-full p-4 border border-slate-200 bg-slate-50 focus:bg-white rounded-2xl text-sm focus:border-blue-400 outline-none transition-all shadow-sm min-h-[100px]" value={formData.instructions} onChange={e => setFormData({...formData, instructions: e.target.value})} />
          </div>
        </div>

        {/* 🔥 BOTONERA CON EL NUEVO TOGGLE PUSH */}
        <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-4 pt-4 border-t border-slate-100">
          
          <div className="flex flex-col sm:flex-row items-center gap-3">
            {/* Toggle Habilitar */}
            <label className="flex items-center gap-3 cursor-pointer group bg-white p-2 pr-4 rounded-xl border border-slate-100 shadow-sm w-full sm:w-auto">
              <div onClick={() => setFormData({...formData, active: !formData.active})} className={`w-12 h-6 rounded-full transition-all relative shadow-inner shrink-0 ${formData.active ? 'bg-green-500' : 'bg-slate-300'}`}>
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all shadow-sm ${formData.active ? 'left-7' : 'left-1'}`}></div>
              </div>
              <span className="text-xs font-black text-slate-600 uppercase tracking-widest">Habilitar en App</span>
            </label>

            {/* Toggle PUSH */}
            <label className="flex items-center gap-3 cursor-pointer group bg-red-50 p-2 pr-4 rounded-xl border border-red-100 shadow-sm w-full sm:w-auto">
              <div onClick={() => setFormData({...formData, is_push: !formData.is_push})} className={`w-12 h-6 rounded-full transition-all relative shadow-inner shrink-0 ${formData.is_push ? 'bg-red-500' : 'bg-red-200'}`}>
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all shadow-sm ${formData.is_push ? 'left-7' : 'left-1'}`}></div>
              </div>
              <span className="text-xs font-black text-red-600 uppercase tracking-widest flex items-center gap-1">
                <Smartphone className="w-3.5 h-3.5" /> Cobro Push
              </span>
            </label>
          </div>

          <div className="flex gap-3 w-full lg:w-auto">
            {editingId && (
              <button onClick={() => {setEditingId(null); setFormData(initialFormState);}} className="flex-1 lg:flex-none px-6 py-3 rounded-xl font-bold text-slate-500 bg-slate-100 hover:bg-slate-200 transition-all">Cancelar</button>
            )}
            <button onClick={handleSave} disabled={loading} className="flex-1 lg:flex-none px-8 py-3 rounded-xl font-black bg-blue-600 text-white hover:bg-blue-700 shadow-[0_10px_20px_rgba(37,99,235,0.2)] disabled:opacity-50 transition-all flex items-center justify-center gap-2 uppercase text-sm tracking-widest active:scale-95">
              <Save className="w-4 h-4" /> {editingId ? 'Actualizar Método' : 'Guardar Método'}
            </button>
          </div>
        </div>
      </div>

      {/* TABLA MEJORADA */}
      <div className="overflow-hidden bg-white rounded-[32px] border border-slate-100 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[800px]">
            <thead>
              <tr className="text-slate-400 text-[10px] font-black uppercase tracking-[2px] border-b bg-slate-50/50">
                <th className="py-5 pl-8">Estado</th>
                <th className="py-5">Origen</th>
                <th className="py-5">Método / Precio</th>
                <th className="py-5">Datos Internos</th>
                <th className="py-5 text-right pr-8">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {methods.map(m => (
                <tr key={m.id} className={`group transition-colors ${!m.active ? 'opacity-60 bg-slate-50/50' : 'hover:bg-blue-50/20'}`}>
                  <td className="py-4 pl-8">
                    <div className={`flex items-center gap-2 font-black text-[9px] uppercase tracking-widest px-3 py-1.5 rounded-lg border w-fit ${m.active ? 'bg-green-50 text-green-600 border-green-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${m.active ? 'bg-green-500 animate-pulse' : 'bg-slate-400'}`}></div>
                      {m.active ? 'Activo' : 'Inactivo'}
                    </div>
                  </td>
                  <td className="py-4">
                    <span className="font-black text-slate-600 bg-slate-100 px-3 py-1.5 rounded-lg text-xs">{m.country_code}</span>
                  </td>
                  <td className="py-4">
                    <div className="flex items-center gap-2">
                      <p className="font-black text-slate-800 text-[15px]">{m.method}</p>
                      {/* 🔥 Etiqueta roja si es PUSH */}
                      {m.is_push && (
                        <span className="bg-red-100 text-red-600 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest flex items-center gap-0.5">
                          <Smartphone className="w-2.5 h-2.5" /> Push
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {Number(m.price) === 0 ? (
                        <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest">Gratis</span>
                      ) : (
                        <span className="text-green-600 font-bold text-xs">Costo: {m.price}</span>
                      )}
                      {m.comment && <span className="text-blue-500 text-[10px] font-bold truncate max-w-[120px]"> • {m.comment}</span>}
                    </div>
                  </td>
                  <td className="py-4">{renderDataSummary(m.data)}</td>
                  <td className="py-4 text-right pr-8 space-x-2">
                    <button onClick={() => handleEdit(m)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all" title="Editar">
                      <Edit2 className="w-5 h-5"/>
                    </button>
                    
                    <button 
                      onClick={() => toggleStatus(m.id, m.active)} 
                      className={`p-2 rounded-xl transition-all ${m.active ? 'text-red-400 hover:text-red-600 hover:bg-red-50' : 'text-green-400 hover:text-green-600 hover:bg-green-50'}`}
                      title={m.active ? "Desactivar para clientes" : "Activar para clientes"}
                    >
                      <Power className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OriginMethods;

